<script setup lang="ts">
import useNetworkStore from '~/store/network'
import { useI18n } from 'vue-i18n'
import { useBreakpoints } from '@vueuse/core'

const { t } = useI18n()
const networkStore = useNetworkStore()
const breakpoints = useBreakpoints({ laptop: 1024 })
const isLaptop = breakpoints.isGreaterOrEqual('laptop')
const cdnUrl = useRuntimeConfig().app.cdnURL

const materialPopupData = {
    imagePath: `${cdnUrl}/images/PopupMaterial.svg`,
    title: t('criteria_popup.material.title'),
    content: [
        t('criteria_popup.material.content1'),
        t('criteria_popup.material.content2'),
        t('criteria_popup.material.content3'),
        t('criteria_popup.material.content4')
    ],
    closeButtonLabel: t('ok')
}

const securityPopupData = {
    imagePath: `${cdnUrl}/images/PopupSecurity.svg`,
    title: t('criteria_popup.security.title'),
    content: [
        t('criteria_popup.security.content1'),
        t('criteria_popup.security.content2'),
        t('criteria_popup.security.content3'),
        t('criteria_popup.security.content4'),
        t('criteria_popup.security.content5')
    ],
    closeButtonLabel: t('ok')
}

const locationPopupData = {
    imagePath: `${cdnUrl}/images/PopupLocation.svg`,
    title: t('criteria_popup.location.title'),
    content: [
        t('criteria_popup.location.content1'),
        t('criteria_popup.location.content2'),
        t('criteria_popup.location.content3')
    ],
    closeButtonLabel: t('ok')
}

const pricePopupData = {
    imagePath: `${cdnUrl}/images/PopupPrice.svg`,
    title: t('criteria_popup.price.title'),
    content: [t('criteria_popup.price.content')],
    closeButtonLabel: t('ok')
}

const clickOnSeeDetails = ref(false)
</script>

<template>
    <div
        class="px-4 md:py-9 bg-white rounded-2xl flex-col justify-center items-center gap-6 md:gap-2 inline-flex"
    >
        <div class="flex-col justify-start items-center gap-1 inline-flex">
            <h3>
                {{ $t('drawer.tabs.reviews') }}
            </h3>
            <img
                class="w-9 order-first md:order-none"
                :src="`${useRuntimeConfig().app.cdnURL}/images/Crown.svg`"
                :alt="$t('drawer.tabs.reviews')"
            />
            <div
                class="flex-col justify-start items-center gap-0.5 inline-flex"
            >
                <span
                    class="text-center text-neutral-900 text-5xl font-bold font-inter"
                >
                    {{
                        $n(networkStore.globalNote, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 1
                        })
                    }}
                </span>
                <span
                    class="text-center text-zinc-500 text-base font-normal font-inter leading-normal"
                >
                    {{
                        $t(
                            'networks.data.ratings_subtitle',
                            networkStore.ratingsCount
                        )
                    }}
                </span>
            </div>
        </div>
        <div
            v-if="!isLaptop"
            class="w-full mx-4 p-2 bg-zinc-100 rounded-2xl flex-col justify-center items-center gap-1.5 inline-flex"
        >
            <RatingDetailPopupItem
                :rating="networkStore.globalNote"
                :label="$t('criteria_popup.global.title')"
                :display-info-icon="false"
            />
            <ChargemapB2CButton
                type="tertiary"
                size="large"
                @click="clickOnSeeDetails = !clickOnSeeDetails"
            >
                <template #icon>
                    <IconChevronDown
                        class="h-6 w-6 transition-transform duration-300 transform-gpu"
                        :class="clickOnSeeDetails && 'rotate-180'"
                    />
                </template>
                {{ $t('word.details') }}
            </ChargemapB2CButton>
        </div>
        <div
            v-if="isLaptop || clickOnSeeDetails"
            class="w-full h-px border border-zinc-300"
        />
        <Transition>
            <div
                v-if="isLaptop || clickOnSeeDetails"
                class="flex-col justify-center items-center gap-6 inline-flex"
            >
                <div class="lg:grid grid-cols-2 gap-x-6 gap-y-2">
                    <RatingDetailPopupItem
                        :rating="networkStore.materialNote"
                        :label="$t('criteria_popup.material.title')"
                        :popup-data="materialPopupData"
                    />
                    <RatingDetailPopupItem
                        :rating="networkStore.locationNote"
                        :label="$t('criteria_popup.location.title_short')"
                        :popup-data="locationPopupData"
                    />
                    <RatingDetailPopupItem
                        :rating="networkStore.priceNote"
                        :label="$t('criteria_popup.price.title')"
                        :popup-data="pricePopupData"
                    />
                    <RatingDetailPopupItem
                        :rating="networkStore.securityNote"
                        :label="$t('criteria_popup.security.title')"
                        :popup-data="securityPopupData"
                    />
                </div>
                <span
                    class="text-center text-zinc-500 text-base font-normal font-inter leading-tight"
                >
                    {{ $t('networks.data.ratings_info') }}
                </span>
            </div>
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
h3 {
    @apply text-center text-xl md:text-2xl leading-tight md:leading-10;
}

.v-enter-active,
.v-leave-active {
    @apply transition-opacity duration-300;
}

.v-enter-from,
.v-leave-to {
    @apply opacity-0;
}
</style>
